/**
 * Segment's analytics.js (Classic v1) library, loaded from Segment's CDN via the snippet in
 * public/index.html, persists the `ajs_user_traits` payload (which contains PII such as email,
 * name, and user ID) in localStorage. Segment's Classic library has no supported configuration
 * option to change this to sessionStorage.
 *
 * Native `Storage` objects (localStorage/sessionStorage) are WebIDL "legacy platform objects":
 * assigning directly to an instance property (e.g. `localStorage.setItem = fn`) is silently
 * ignored, so the only reliable way to intercept calls is to patch the shared `Storage.prototype`
 * methods themselves and use the `this` binding to tell localStorage and sessionStorage apart.
 * Only the `ajs_user_traits` key is redirected to sessionStorage; every other localStorage key
 * (including other Segment `ajs_*` keys) continues to use localStorage exactly as before.
 */
export const AJS_USER_TRAITS_KEY = 'ajs_user_traits';

/**
 * Builds a patched replacement for a Storage.prototype method (setItem/getItem/removeItem) that
 * redirects calls made against `localStorageInstance` for AJS_USER_TRAITS_KEY to
 * `sessionStorageInstance`, and otherwise behaves exactly like the original method.
 */
export const createInterceptedStorageMethod = (originalMethod, localStorageInstance, sessionStorageInstance) => (
    function ajsUserTraitsInterceptedMethod(key, ...rest) {
        if (this === localStorageInstance && key === AJS_USER_TRAITS_KEY) {
            return originalMethod.apply(sessionStorageInstance, [key, ...rest]);
        }
        return originalMethod.apply(this, [key, ...rest]);
    }
);

/**
 * One-time migration for users who already have ajs_user_traits persisted in localStorage from
 * before this interceptor was installed.
 */
export const migrateExistingTraits = (localStorageInstance, sessionStorageInstance) => {
    const existingValue = localStorageInstance.getItem(AJS_USER_TRAITS_KEY);
    if (existingValue !== null && existingValue !== undefined) {
        sessionStorageInstance.setItem(AJS_USER_TRAITS_KEY, existingValue);
        localStorageInstance.removeItem(AJS_USER_TRAITS_KEY);
    }
};

const PATCHED_STORAGE_METHODS = ['setItem', 'getItem', 'removeItem'];

export const installAjsUserTraitsStorageInterceptor = () => {
    if (typeof window === 'undefined' || typeof Storage === 'undefined') {
        return;
    }

    try {
        // Accessing these can throw in some browsers / private modes.
        if (!window.localStorage || !window.sessionStorage) {
            return;
        }
    } catch (e) {
        return;
    }

    if (Storage.prototype.setItem && Storage.prototype.setItem.__ajsUserTraitsPatched) {
        return;
    }

    try {
        migrateExistingTraits(window.localStorage, window.sessionStorage);
    } catch (error) {
        // do nothing
    }

    try {
        PATCHED_STORAGE_METHODS.forEach((methodName) => {
            const originalMethod = Storage.prototype[methodName];
            const patchedMethod = createInterceptedStorageMethod(
                originalMethod,
                window.localStorage,
                window.sessionStorage,
            );
            patchedMethod.__ajsUserTraitsPatched = true;
            Storage.prototype[methodName] = patchedMethod;
        });
    } catch (error) {
        // do nothing
    }
};

